import { useCallback } from 'react';

import { useEmailCampaigns } from '../models/emailCampaigns/queries';

/**
 * Look up which active email campaigns a contact list feeds.
 *
 * Adding recipients to such a list makes the campaign schedule sends for them, so callers use this
 * to warn before the fact. Derived client-side: the campaigns payload already carries both the
 * status and the attached list ids, so no extra request is needed.
 *
 * Returns a lookup rather than the campaigns for one list, so a caller can resolve a list the user
 * picks without waiting for a re-render.
 *
 * Callers must gate their submit on `isLoading`: until the campaigns arrive the lookup cannot tell
 * "no active campaign" apart from "not loaded yet", and would silently skip the warning.
 *
 * @returns {{getActiveCampaignsForList: (listId: string) => Array<{id: string, name: string}>, isLoading: boolean}}
 */
export function useActiveCampaignsForList() {
	const { data: campaigns = [], isLoading } = useEmailCampaigns();

	const getActiveCampaignsForList = useCallback(
		(listId) => {
			if (!listId) return [];

			return campaigns
				.filter(
					(campaign) =>
						campaign.campaignStatus === 'active' &&
						campaign.emailCampaign_emailList?.some((link) => link.contactLists?.id === listId),
				)
				.map((campaign) => ({ id: campaign.id, name: campaign.campaignName }));
		},
		[campaigns],
	);

	return { getActiveCampaignsForList, isLoading };
}

export default useActiveCampaignsForList;
